<template>
    <div>
        <v-form ref="form">
            <slot name="form-fields">
                <!-- Auto-generated form fields based on debugMethod.form -->
                <v-row v-if="debugMethod.form && Object.keys(debugMethod.form).length > 0">
                    <v-col
                        v-for="(field, fieldName) in debugMethod.form"
                        :key="fieldName"
                        cols="12"
                        :sm="field.type === 'checkbox' ? 12 : field.type === 'textarea' ? 12 : field.type === 'datetime' ? 6 : 4"
                    >
                    <!-- Text field -->
                    <v-text-field
                        v-if="field.type === 'text'"
                        v-model="formData[field.name || fieldName]"
                        :name="field.name || fieldName"
                        :label="field.label"
                        :placeholder="field.placeholder"
                        :hint="field.description"
                        :required="field.required || false"
                        :rules="getFieldRules(field, fieldName)"
                        persistent-hint
                        dense
                    ></v-text-field>

                    <!-- Textarea field -->
                    <v-textarea
                        v-else-if="field.type === 'textarea'"
                        v-model="formData[field.name || fieldName]"
                        :name="field.name || fieldName"
                        :label="field.label"
                        :placeholder="field.placeholder"
                        :hint="field.description"
                        :required="field.required || false"
                        :rules="getFieldRules(field, fieldName)"
                        persistent-hint
                        dense
                        rows="5"
                    ></v-textarea>

                    <!-- Datetime field -->
                    <div v-else-if="field.type === 'datetime'" class="d-flex">
                        <v-icon class="mr-3">mdi-calendar</v-icon>
                        <v-datetime-picker
                            v-model="formData[field.name || fieldName]"
                            :name="field.name || fieldName"
                            :label="field.label"
                            :hint="field.description"
                            timeFormat="HH:mm:ss"
                        ></v-datetime-picker>
                    </div>

                    <!-- Checkbox field -->
                    <v-checkbox
                        v-else-if="field.type === 'checkbox'"
                        v-model="formData[field.name || fieldName]"
                        :name="field.name || fieldName"
                        :label="field.label"
                        :hint="field.description"
                        :required="field.required || false"
                        :rules="field.required ? [v => !!v || field.label + ' is required'] : []"
                        persistent-hint
                        dense
                    ></v-checkbox>

                        <!-- Add more field types as needed -->
                    </v-col>
                </v-row>
            </slot>
        </v-form>

        <slot name="actions" class="mt-3">
            <v-btn
                small
                :disabled="isRunning"
                color="primary"
                class="my-3"
                @click="executeMethod(debugMethod.method)"
                >
                    <span v-if="isRunning">
                        <v-progress-circular
                            indeterminate
                            size="12"
                            width="2"
                            color="primary darken-3">
                        </v-progress-circular>
                        Executing...
                    </span>
                    <span v-else>Execute</span>
            </v-btn>

            <v-btn class="my-3"
                v-if="cancelTokenSource"
                small
                @click="cancelRequest">
                Cancel
            </v-btn>

            <v-btn class="my-3"
                v-if="result && !isRunning"
                small
                @click="copyToClipboard(result)">
                {{ copyBtnText }}
            </v-btn>
        </slot>

        <slot name="result">
            <v-card
                v-if="result"
                class="mt-3 pa-3"
                color="grey lighten-4"
            >
                <pre>{{ result }}</pre>
            </v-card>
        </slot>

    </div>
</template>
<style scoped>
/* Make v-card resizable */
.v-card {
  resize: vertical;      /* Allows vertical drag to resize */
  overflow: auto;        /* Needed so content can scroll after resizing */
  max-height: 80vh;     /* Optional cap */
}

.v-card pre {
  font-family: monospace;
  white-space: pre-wrap;
  margin: 0;
}
</style>

<script>
import axios from "axios";
import { EventBus } from '@/js/event-bus.js'
import moment from 'moment';
const COPY_BTN_TEXT = "Copy Result";
export default {
    props: {
        debugMethod: { type: Object, required: true },
        formData: { type: Object, default: () => ({}) },
        integration: { type: Object, required: false }
    },
    data() {
        return {
            isRunning: false,
            baseUri: `/api/debug-tool/${this.$route.params.id}`,
            result: null,
            cancelTokenSource: null, // store cancel token
            copyBtnText: COPY_BTN_TEXT,
        }
    },

    mounted() {
        // Initialize default values for datetime fields if not already set
        if (this.debugMethod.form) {
            Object.keys(this.debugMethod.form).forEach(fieldName => {
                const field = this.debugMethod.form[fieldName];
                const fieldKey = field.name || fieldName;

                // Set default datetime values if not already set
                if (field.type === 'datetime' && !this.formData[fieldKey]) {
                    if (fieldKey === 'period_start') {
                        this.$set(this.formData, fieldKey, new Date(moment().subtract(1, 'days')));
                    } else if (fieldKey === 'period_end') {
                        this.$set(this.formData, fieldKey, new Date(moment()));
                    }
                }

                // Set default checkbox values if defined
                if (field.type === 'checkbox' && this.formData[fieldKey] === undefined && field.default !== undefined) {
                    this.$set(this.formData, fieldKey, field.default);
                }

                // Set default textarea/text values if defined
                if ((field.type === 'textarea' || field.type === 'text') && this.formData[fieldKey] === undefined && field.default !== undefined) {
                    this.$set(this.formData, fieldKey, field.default);
                }
            });
        }
    },

    methods: {
        getFieldRules(field, fieldName) {
            const rules = [];

            // Standard required validation
            if (field.required) {
                rules.push(v => !!v || field.label + ' is required');
            }

            // RequiredWith validation: at least one of the fields must be filled
            if (field.requiredWith && Array.isArray(field.requiredWith)) {
                rules.push(v => {
                    // If current field has value, it's valid
                    if (v) return true;

                    // Check if any of the related fields have values
                    const hasAnyValue = field.requiredWith.some(relatedFieldName => {
                        const relatedValue = this.formData[relatedFieldName];
                        return relatedValue !== undefined && relatedValue !== null && relatedValue !== '';
                    });

                    // If no related field has value, show error
                    if (!hasAnyValue) {
                        return 'At least one field must be filled';
                    }

                    return true;
                });
            }

            return rules;
        },
        executeMethod(method) {
            // Validate form using Vuetify's validation
            if (this.$refs.form && !this.$refs.form.validate()) {
                return;
            }

            this.isRunning = true;
            this.formData.method = method;

            // create a cancel token source
            this.cancelTokenSource = axios.CancelToken.source();

            this.$http
                .post(`${this.baseUri}`, this.formData, {
                    cancelToken: this.cancelTokenSource.token,
                })
                .then(({ data }) => {
                    this.result = JSON.stringify(data, null, 2);
                })
                .catch(err => {
                    if (this.$http.isCancel && this.$http.isCancel(err)) {
                        this.result = "❌ Request canceled by user.";
                    } else if (err.response) {
                        // Server responded with a status code outside 2xx
                        this.result = JSON.stringify(err.response.data, null, 2);
                    } else if (err.request) {
                        // Request was made but no response
                        this.result = "⚠️ No response received from server.";
                    } else {
                        // Something happened setting up the request
                        this.result = err.message;
                    }
                })
                .finally(() => {
                    this.isRunning = false;
                    this.cancelTokenSource = null
                });
        },

        cancelRequest() {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel("Request canceled by user");
                this.cancelTokenSource = null; // reset the cancel token source
            }
        },

        copyToClipboard(copiedTxt) {

            if (navigator.clipboard && navigator.clipboard.writeText) {

                const content = copiedTxt.replace(/\t/g, '');
                navigator.clipboard.writeText(content)
                    .catch((err) => {
                        console.error('Unable to copy text to clipboard', err);
                        EventBus.$emit('show-error', err);
                    })
                    .finally(() => {
                        this.copyBtnText = "Copied to clipboard";
                        setTimeout(() => {
                            this.copyBtnText = COPY_BTN_TEXT;
                        }, 2000); // reset button text after 2 seconds
                    });
            } else {
                EventBus.$emit('show-error', 'Clipboard API not supported in this environment.');
                this.copyBtnText = COPY_BTN_TEXT;
            }
        },
    }
}
</script>